import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { logApplicationFailure } from "@/lib/user-error";

// SHARE · Discover - read-only attachment list on the detail view. Mirrors the
// attachments the owner adds in ShareCreatePage, which were previously only
// visible while editing. Both actions are lazy (see useSharedItem): the bytes
// are fetched on click, not on page load. "Open" previews the file in a new
// tab (no download needed); "Download" saves it.

export interface ShareAttachment {
  id: string;
  fileName?: string | null;
  mimeType?: string | null;
  fileExtension?: string | null;
}

type Action = "open" | "download";

function OpenGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden
    >
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

function DownloadGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden
    >
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

export function ShareItemAttachments({
  files,
  onOpen,
  onDownload,
}: {
  files: ReadonlyArray<ShareAttachment>;
  onOpen: (fileId: string) => Promise<void>;
  onDownload: (fileId: string) => Promise<void>;
}) {
  const intl = useIntl();
  const [busy, setBusy] = useState<{ id: string; action: Action } | null>(null);
  const [failed, setFailed] = useState<{ id: string; action: Action } | null>(null);

  if (files.length === 0) return null;

  async function run(id: string, action: Action) {
    if (busy) return;
    setBusy({ id, action });
    setFailed(null);
    try {
      await (action === "open" ? onOpen(id) : onDownload(id));
    } catch {
      logApplicationFailure(`share.${action}-attachment`);
      setFailed({ id, action });
    } finally {
      setBusy(null);
    }
  }

  const unnamed = intl.formatMessage({
    id: "share.itemDetail.attachmentUnnamed",
    defaultMessage: "Attachment",
  });

  return (
    <div className="mt-6 border-t border-ink/5 pt-5">
      <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/45">
        <FormattedMessage id="share.itemDetail.attachments" defaultMessage="Attachments" /> · {files.length}
      </div>
      <ul className="space-y-2">
        {files.map((f, i) => {
          const name = f.fileName || `${unnamed} ${i + 1}`;
          const ext = (f.fileExtension || "").replace(/^\./, "").toUpperCase();
          const rowBusy = busy?.id === f.id;
          const openLabel = intl.formatMessage(
            { id: "share.itemDetail.openAttachment", defaultMessage: "Open {name}" },
            { name },
          );
          const downloadLabel = intl.formatMessage(
            { id: "share.itemDetail.downloadAttachment", defaultMessage: "Download {name}" },
            { name },
          );
          return (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-md bg-page px-3 py-2 ring-1 ring-ink/10 transition hover:ring-ink/20"
            >
              <span className="field-label flex h-8 w-8 shrink-0 items-center justify-center rounded bg-ink/[0.06] uppercase tracking-wider text-ink/70 ring-1 ring-ink/5">
                {ext || "•"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium">{name}</div>
                {failed?.id === f.id ? (
                  <div className="text-[10px] text-red-600">
                    {failed.action === "open" ? (
                      <FormattedMessage
                        id="share.itemDetail.attachmentOpenFailed"
                        defaultMessage="Open failed."
                      />
                    ) : (
                      <FormattedMessage
                        id="share.itemDetail.attachmentDownloadFailed"
                        defaultMessage="Download failed."
                      />
                    )}
                  </div>
                ) : f.mimeType ? (
                  <div className="truncate text-[10px] text-ink/45">{f.mimeType}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="icon"
                  size="sm"
                  onClick={() => void run(f.id, "open")}
                  disabled={rowBusy}
                  className="disabled:opacity-50"
                  aria-label={openLabel}
                  title={openLabel}
                >
                  {busy?.id === f.id && busy.action === "open" ? "…" : <OpenGlyph />}
                </Button>
                <Button
                  variant="icon"
                  size="sm"
                  onClick={() => void run(f.id, "download")}
                  disabled={rowBusy}
                  className="disabled:opacity-50"
                  aria-label={downloadLabel}
                  title={downloadLabel}
                >
                  {busy?.id === f.id && busy.action === "download" ? "…" : <DownloadGlyph />}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
